import { compactFlatten, object, toDate } from '@art-suite/art-core-ts';
import {
  ApiCreateOrgProduct,
  ApiOrgProduct,
  ApiScan,
  ApiUpdateOrgProduct,
  getOrgProductQueryKey,
  getOrgProductQueryKeyPrefix,
  getOrgProductsFilterQueryKey,
  getOrgProductsQueryKeyWithArchived,
  getScanTemplateRootQueryKey,
  KaiProApiClient,
} from '@kaiizen/kai-pro-api';
import { getAllSkuIdOptions, getSkuIdFromSkuOption } from '@kaiizen/kai-scan-template-system';
import { isCacheEntryFresh, QueryCache, useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';
import { useMemo } from 'react';
import { ScanOfflineDataMinTtlOnAccessMs } from '../apiScanAndTemplateState/ScanStateLib';
import { useAuth } from '../useAuth';
import { useKaiProApiClient } from '../useKaiProApiClient';

/** How long a cached `OrgProduct` is trusted before it's treated as stale and refetched. */
export const OrgProductStaleAgeMs = 1000 * 60 * 60 * 24; // 24 hours

export type OrgProductMutators = {
  create: (record: ApiCreateOrgProduct) => Promise<ApiOrgProduct>;
  update: (id: string, updates: ApiUpdateOrgProduct) => Promise<ApiOrgProduct>;
};

const getOrgProductMutators = (kaiProApiClient: KaiProApiClient, orgId: string | undefined, queryCache: QueryCache) => {
  const orgProductQueryCacheKeys: string[] = [getOrgProductsQueryKeyWithArchived(orgId, true), getOrgProductsQueryKeyWithArchived(orgId, false)];
  const updateCache = async (_updatedOrgProduct: Promise<ApiOrgProduct>) => {
    const updatedOrgProduct = await _updatedOrgProduct;
    await queryCache.cache.set(getOrgProductQueryKey(updatedOrgProduct.id), updatedOrgProduct);
    await Promise.all(orgProductQueryCacheKeys.map(async key => {
      const current = await queryCache.cache.getEntry(key);
      if (current) {
        queryCache.cache.set(key, [updatedOrgProduct, ...(current.value as ApiOrgProduct[]).filter(p => p.id !== updatedOrgProduct.id)]);
      }
    }));
    return updatedOrgProduct;
  };
  return {
    create: async (record: ApiCreateOrgProduct) => updateCache(kaiProApiClient.vivifyOrgProduct(record)),
    update: async (id: string, updates: ApiUpdateOrgProduct) => updateCache(kaiProApiClient.updateOrgProduct(id, updates)),
  };
};

const fetchAndPopulateSingletonProductQueries = async (orgProductsPromise: Promise<ApiOrgProduct[]>, queryCache: QueryCache): Promise<ApiOrgProduct[]> =>
  orgProductsPromise
    .then(orgProducts => {
      const result = orgProducts.map(async orgProduct => {
        await queryCache.cache.set(getOrgProductQueryKey(orgProduct.id), orgProduct);
        return orgProduct;
      });
      return Promise.all(result);
    });

export const useOrgProducts = ({ archived = false }: { archived?: boolean } = {}) => {
  const auth = useAuth();
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache();
  const queryKey = getOrgProductsQueryKeyWithArchived(auth.orgId, archived);

  const query = useCachedQuery<ApiOrgProduct[]>(
    queryKey,
    (key, queryCache) =>
      fetchAndPopulateSingletonProductQueries(kaiProApiClient.getOrgProducts({ archived: archived ? true : undefined }), queryCache as QueryCache<unknown>),
    { enabled: !!kaiProApiClient.token && !!auth.orgId },
  );

  const orgProducts = query.data;
  const orgProductsById = useMemo(() => orgProducts ? object(orgProducts, { withKey: ({ id }) => id }) : undefined, [orgProducts]);

  const orgProductMutators = getOrgProductMutators(kaiProApiClient, auth.orgId, queryCache);
  return {
    ...query,
    orgProducts,
    orgProductsById,
    orgProductMutators,
  };
};

export const useOrgProductsWithFilter = ({ archived = false, queryString, limit = 250 }: { archived?: boolean; queryString?: string; limit?: number } = {}) => {
  const auth = useAuth();
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache();
  const options = { archived, queryString, limit };
  const queryKey = getOrgProductsFilterQueryKey(auth.orgId, options);

  const query = useCachedQuery<ApiOrgProduct[]>(
    queryKey,
    (key, queryCache) =>
      fetchAndPopulateSingletonProductQueries(
        kaiProApiClient.getOrgProducts({
          archived: archived ? true : undefined,
          queryString,
          limit,
        }),
        queryCache as QueryCache<unknown>,
      ),
    {
      enabled: !!kaiProApiClient.token && !!auth.orgId,
      staleAgeMs: 0,
      maxAgeMs: 0,
    },
  );

  const orgProducts = query.data;
  const orgProductsById = useMemo(() => orgProducts ? object(orgProducts, { withKey: ({ id }) => id }) : undefined, [orgProducts]);

  const orgProductMutators = getOrgProductMutators(kaiProApiClient, auth.orgId, queryCache);
  return {
    ...query,
    orgProducts,
    orgProductsById,
    orgProductMutators,
  };
};

/**
 * Tracks org product fetches currently in flight, keyed by id, so that a batch fetch
 * (bulkFetchProductBatch) and an individual fetch (useOrgProduct) started concurrently for
 * the same id join a single request instead of both hitting the API.
 */
const activeOrgProductLoads = new Map<string, Promise<ApiOrgProduct>>();

const getActiveOrgProductLoad = (id: string): Promise<ApiOrgProduct> | undefined => activeOrgProductLoads.get(id);

/** Registers `promise` as the in-flight load for `id`, clearing it once settled. */
const registerOrgProductLoad = (id: string, promise: Promise<ApiOrgProduct>): void => {
  activeOrgProductLoads.set(id, promise);
  promise
    .finally(() => {
      if (activeOrgProductLoads.get(id) === promise) activeOrgProductLoads.delete(id);
    })
    .catch(() => {
      // ignored here - callers that actually need the result await `promise` directly and see the rejection there
    });
};

export const useOrgProduct = (id: string | null | undefined) => {
  const kaiProApiClient = useKaiProApiClient();
  const auth = useAuth();
  const queryCache = useQueryCache();

  const query = useCachedQuery(
    getOrgProductQueryKey(id!),
    () => {
      const activeLoad = getActiveOrgProductLoad(id!);
      if (activeLoad) return activeLoad;
      const promise = kaiProApiClient.getOrgProduct(id!);
      registerOrgProductLoad(id!, promise);
      return promise;
    },
    { enabled: !!kaiProApiClient.token && !!id, staleAgeMs: OrgProductStaleAgeMs },
  );

  return {
    ...query,
    orgProduct: query.data,
    orgProductMutators: getOrgProductMutators(kaiProApiClient, auth.orgId, queryCache),
  };
};

/** @deprecated use useOrgProduct instead */
export const useCachedOrgProduct = (id: string | undefined) => useOrgProduct(id);

/**
 * Bulk fetch one batch of org products, avoiding duplicate simultaneous fetches.
 * @param kaiProApiClient - The KaiProApiClient to use.
 * @param ids - The ids of the org products to fetch.
 * @returns The org products.
 * @throws {Error} if the fetch fails.
 * @throws {Error} if the fetch fails.
 */
const bulkFetchProductBatch = async (kaiProApiClient: KaiProApiClient, ids: string[]): Promise<ApiOrgProduct[]> => {
  const alreadyLoadingIds = ids.filter(id => activeOrgProductLoads.has(id));
  const newIds = ids.filter(id => !activeOrgProductLoads.has(id));

  console.log(
    `useOrgProductsByIdForTemplate: bulkFetchProducts: alreadyLoadingIds: ${alreadyLoadingIds.length}, newIds: ${newIds.length} - PREFETCH_SIZE: ${PREFETCH_SIZE}`,
  );

  const newProductLoadsPromise: Promise<ApiOrgProduct[]> = kaiProApiClient.getOrgProductBatch(newIds);
  for (const newId of newIds) {
    registerOrgProductLoad(newId, newProductLoadsPromise.then(products => products.find(p => p.id === newId)!));
  }
  const existingProductLoadsPromise = Promise.all(alreadyLoadingIds.map(id => activeOrgProductLoads.get(id)!));
  return compactFlatten(await Promise.all([existingProductLoadsPromise, newProductLoadsPromise]));
};

const PREFETCH_SIZE = 50;

/**
 * Bulk fetch org products in batches, avoiding duplicate simultaneous fetches.
 * @param kaiProApiClient - The KaiProApiClient to use.
 * @param ids - The ids of the org products to fetch.
 * @param setProgress - The function to set the progress.
 * @param orgProductsById - The map of org products by id.
 * @param orgProductQueryCache - The query cache to use.
 * @param totalCount - The total count of org products to fetch.
 * @returns The org products.
 */
const bulkFetchProducts = async (
  kaiProApiClient: KaiProApiClient,
  ids: string[],
  setProgress: (progress: number) => void,
  orgProductsById: Record<string, ApiOrgProduct>,
  orgProductQueryCache: QueryCache<ApiOrgProduct>,
  totalCount: number,
): Promise<ApiOrgProduct[]> => {
  // for testing, randomly fail 10% of the time
  const results: ApiOrgProduct[] = [];
  for (let i = 0; i < ids.length; i += PREFETCH_SIZE) {
    const products = await bulkFetchProductBatch(kaiProApiClient, ids.slice(i, i + PREFETCH_SIZE));
    for (const product of products) {
      orgProductsById[product.id] = product;
      orgProductQueryCache.cache.set(getOrgProductQueryKey(product.id), product);
    }
    const effectiveCompletedCount = i + (totalCount - ids.length);
    setProgress(effectiveCompletedCount / totalCount);
  }
  // we're doing some tricky things, so double check results
  for (const id of ids) {
    if (!orgProductsById[id]) {
      console.warn(`useOrgProductsByIdForTemplate: bulkFetchProducts: product ${id} not found in results`);
    }
  }
  return results;
};

export const useOrgProductsByIdForTemplate = (scanTemplateId: string | undefined, versionAt?: Date, prefetchOnly: boolean = false) => {
  const kaiProApiClient = useKaiProApiClient();
  const query = useCachedQuery<{ count: number; time: number; orgProductsById: Record<string, ApiOrgProduct> | undefined }>(
    `orgProductPrepForScan/${scanTemplateId}${`?versionAt=${versionAt ? toDate(versionAt).toISOString() : 'N/A'}`}&prefetchOnly=${!!prefetchOnly}`,
    async (key, queryCache, { setProgress }) => {
      const orgProductQueryCache = queryCache as unknown as QueryCache<ApiOrgProduct>;
      const startTime = Date.now();
      const scanTemplateRoot = await queryCache.getOrFetch(
        getScanTemplateRootQueryKey(scanTemplateId, versionAt),
        () => kaiProApiClient.getScanTemplateRoot(scanTemplateId!, versionAt),
      );
      // a template names its products either by id or by carrying the whole product inline; both forms need the live product fetched
      const allOrgProductIdsForTemplate = new Set(getAllSkuIdOptions(scanTemplateRoot).map(getSkuIdFromSkuOption));
      const productIdsToFetch: string[] = [];
      const orgProductsById: Record<string, ApiOrgProduct> = {};

      if (prefetchOnly) {
        const orgProductQueryKeyPrefix = getOrgProductQueryKeyPrefix();
        const keys = await orgProductQueryCache.cache.getAllKeys(orgProductQueryKeyPrefix);
        const ids = new Set(keys.map(key => key.split(orgProductQueryKeyPrefix).pop() as string));
        allOrgProductIdsForTemplate.forEach(id => {
          if (!ids.has(id)) {
            productIdsToFetch.push(id);
          }
        });
      } else {
        for (const id of allOrgProductIdsForTemplate) {
          const orgProduct = await orgProductQueryCache.cache.getEntry(getOrgProductQueryKey(id));
          if (orgProduct?.value && isCacheEntryFresh(orgProduct, OrgProductStaleAgeMs)) {
            orgProductsById[id] = orgProduct.value;
            continue;
          }
          productIdsToFetch.push(id);
        }
      }
      if (productIdsToFetch.length > 0) {
        await bulkFetchProducts(kaiProApiClient, productIdsToFetch, setProgress, orgProductsById, orgProductQueryCache, allOrgProductIdsForTemplate.size);
      }
      const endTime = Date.now();
      return { count: allOrgProductIdsForTemplate.size, time: endTime - startTime, orgProductsById: !prefetchOnly ? orgProductsById : undefined };
    },
    {
      enabled: !!kaiProApiClient.token && !!scanTemplateId,
      maxRetryDelayMs: 5000,
      minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs,
    },
  );
  return {
    ...query,
    ...query.data,
  };
};

export const useOrgProductsByIdForScan = (scan: ApiScan | undefined, prefetchOnly: boolean = false) =>
  useOrgProductsByIdForTemplate(scan?.scanTemplateId, scan?.scanTemplateAt, prefetchOnly);
